import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Radio,
  notification,
  Select,
  Popover
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import UserAPI from "../../../restAPI/UserAPI";
import validationRules from "../../../assets/validation/rules";
import validateForm from "../../../utils/FormValidation";

const popoverContentType = (
  <div>
1. <b>Group Admin:</b> Full permissions &
group-level access <br/>
2. <b>Company Admin:</b> Partial
permissions & company-level
access only <br/>
3. <b>General User:</b> Only certain
permissions
  </div>
);
const Context = React.createContext({
  name: "Default",
});

const StepForm = forwardRef(({ onRefresh }, ref) => {
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector((state) => state.auth);
  const [userTypes, setUserTypes] = useState([]);
  const [isGetUserTypes, setIsGetUserTypes] = useState(true);
  const [payload, setPayload] = useState({
    _id: "",
    firstName: "",
    lastName: "",
    email: "",
    roleId: "",
    role: "",
    groupId: localStorage.getItem("groupId"),
    companyId: localStorage.getItem("companyId"),
    status: 1,
  });
  const [errors, setErrors] = useState({});

  const [api,contextHolder] = notification.useNotification();
  const notificationShown = useRef(false);

  const openNotificationWithIcon = (type, message, description, placement) => {
    if (!notificationShown.current) {
      notification[type]({
        message: message,
        description: description,
        placement,
      });
      notificationShown.current = true;
      setTimeout(() => {
        notificationShown.current = false;
      }, 5000);
    }
  };

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openDrawer: () => {
      setVisible(true); // Open drawer
    },
    closeDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  const contextValue = useMemo(
    () => ({
      name: "Ant Design",
    }),
    []
  );

  const onClose = () => {
    setVisible(false);
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    setPayload({
      _id: "",
      firstName: "",
      lastName: "",
      email: "",
      roleId: "",
      role: "",
      groupId: localStorage.getItem("groupId"),
      companyId: localStorage.getItem("companyId"),
      status: 1,
    });
    setErrors({});
  };

  useEffect(() => {
    if (isGetUserTypes) {
      getAllUserTypes();
      setIsGetUserTypes(false);
    }
  });

  const getAllUserTypes = async () => {
    await UserAPI.getAllUserTypes(
      token,
      localStorage.getItem("groupId"),
      localStorage.getItem("companyId")
    )
      .then((res) => {
        if (res?.data) {
          
          const desiredOrder = ["Group Admin", "Company Admin", "General User"];
          res?.data.sort((a, b) => 
            desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name)
          );
          
          const rows = [];
          res.data.map((rowValue) => {
            rows.push({ value: rowValue._id, label: rowValue.name });
          });
          setUserTypes(rows);
        }
      })
      .catch((err) => {
        console.log("error getting contry codes data :", err.message);
      });
  };

  const OnUserTypeChange = (value) => {
    let userType = userTypes.filter(function (obj) {
      return obj.value === value;
    });

    if (userType.length > 0) {
      userType.map((rowValue) => {
        setPayload({
          _id: payload._id,
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
          roleId: value,
          role: rowValue.label,
          groupId: payload.groupId,
          companyId: payload.companyId,
          status: payload.status,
        });
      });
      setErrors({});
    }
  };

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules);
    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
    } else {
      const keyData = Object.keys(data)[0];
      setErrors((prev) => ({ ...prev, [keyData]: "" }));
    }
  }

  const onRadioChange = (e) => {
    setPayload({
      _id: payload._id,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      roleId: payload.roleId,
      role: payload.role,
      groupId: payload.groupId,
      companyId: payload.companyId,
      status: e.target.value,
    });
  };

  const onClearChange = () => {
    setPayload({
      _id: "",
      firstName: "",
      lastName: "",
      email: "",
      roleId: "",
      role: "",
      groupId: "",
      companyId: "",
      status: 1,
    });
    setErrors({});
  };

  const validateBeforeSubmit = () => {
    const validateData = {
      userFirstName: payload.firstName,
      userLastName: payload.lastName,
      email: payload.email,
      userType: payload.roleId,
    };
    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
        newErrors[value] = validationErr[value];
      });
      setErrors(newErrors);
      return false;
    } else {
      return true;
    }
  };

  const onSubmit = async () => {
    if (localStorage.getItem("groupId") === null) {
      openNotificationWithIcon(
        "error",
        "User creation failed",
        "Please select the group",
        "bottomLeft"
      );
    } else {
      //console.log(JSON.stringify(payload));
      if (localStorage.getItem("companyId") === null) {
        if (
          payload.role === "General User" ||
          payload.role === "Company Admin"
        ) {
          openNotificationWithIcon(
            "error",
            "User creation failed",
            "Please select the company",
            "bottomLeft"
          );
        } else if (payload.role === "Group Admin") {
          const validationStatus = validateBeforeSubmit();
          if (validationStatus) {
            createUser();
          }
        }
      } else {
        const validationStatus = validateBeforeSubmit();
        if (validationStatus) {
          createUser();
        }
      }
    }
  };

  const createUser = async () => {
    //alert("Company Fail");
    const formData = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      roleId: payload.roleId,
      groupId: localStorage.getItem("groupId"),
      companyId: localStorage.getItem("companyId"),
      status: payload.status,
    };
    //console.log(formData);
    await UserAPI.createUser(
      token,
      localStorage.getItem("groupId"),
      localStorage.getItem("companyId"),
      formData
    )
      .then((response) => {
        //console.log(response);
        onClose();
        openNotificationWithIcon(
          "success",
          "Successful",
          `${response.message}`,
          "bottomLeft"
        );
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon(
            "error",
            "User creation failed",
            `${err.response.data.message}`,
            "bottomLeft"
          );
        } else {
          openNotificationWithIcon(
            "error",
            "User creation failed",
            `${err.message}`,
            "bottomLeft"
          );
        }
      });
  };

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title="Create User"
        width={700}
        visible={visible}
        onClose={onClose}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button style={{ marginRight: 8 }} size="large" onClick={onClearChange}>
            Reset
            </Button>

            <Button
              style={{ marginRight: 8, display: "none" }}
              size="large"
              onClick={onClearChange}>
              Clear
            </Button>

            <Button type="primary" size="large" onClick={onSubmit}>
              Submit
            </Button>
          </div>
        }
      >
        <div style={{ marginTop: 24 }}>
          <Form layout="vertical">
            {/* <Alert message="* Fields are required" type="warning" /> */}
            <Form.Item
              label="First Name"
              validateStatus={errors.userFirstName ? "error" : ""}
              help={errors.userFirstName || ""}
              required
            >
              <Input
                size="large"
                name="firstName"
                value={payload["firstName"]}
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }));
                  const validateData = { userFirstName: e.target.value };
                  validateFormData(validateData);
                }}
                placeholder="First Name"
              />
            </Form.Item>

            <Form.Item
              label="Last Name"
              validateStatus={errors.userLastName ? "error" : ""}
              help={errors.userLastName || ""}
              required
            >
              <Input
                size="large"
                name="lastName"
                value={payload["lastName"]}
                onChange={(e) => {
                  setPayload((prev) => ({ ...prev, lastName: e.target.value }));
                  const validateData = { userLastName: e.target.value };
                  validateFormData(validateData);
                }}
                placeholder="Last Name"
              />
            </Form.Item>

            <Form.Item
              label="Email Address"
              validateStatus={errors.email ? "error" : ""}
              help={errors.email || ""}
              required
            >
              <Input
                size="large"
                name="email"
                value={payload["email"]}
                onChange={(e) => {
                  setPayload((prev) => ({ ...prev, email: e.target.value }));
                  const validateData = { email: e.target.value };
                  validateFormData(validateData);
                }}
                placeholder="Email Address"
              />
            </Form.Item>

            <Form.Item
             
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  User Type
                  <Popover content={popoverContentType} title="User Type" trigger="hover">
                    <InfoCircleOutlined className="info-icon" />
                  </Popover>
                </div>
              }
              validateStatus={errors.userType ? "error" : ""}
              help={errors.userType || ""}
              required
            >
              <Select
                showSearch
                name="userType"
                placeholder="Select User Type"
                optionFilterProp="label"
                value={payload["roleId"] || "Select a User Type"}
                size="large"
                onChange={OnUserTypeChange}
                options={userTypes}
              />
            </Form.Item>
            <Form.Item label="Status" required>
              <Radio.Group
                name="status"
                value={payload["status"]}
                onChange={onRadioChange}
              >
                <Radio value={1}>Active</Radio>
                <Radio value={0}>Inactive</Radio>
              </Radio.Group>
            </Form.Item>
          </Form>
        </div>
      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
